import { getStatusModifier } from '../resident-report-detail/map-report-detail';
import {
  MANAGER_REPORT_STATUS_OPTIONS,
  type ManagerReportRow,
  type ManagerReportSignCheckResult,
  type ManagerReportsFilter,
} from './types';
import { getManagerReportArchiveUrl } from './manager-report-urls';

export interface ManagerReportDetailMetricRow {
  label: string;
  plan: string;
  fact: string;
  deviation: string;
}

export interface ManagerReportDetailDocument {
  name: string;
  url: string;
  format: string;
  size: string;
}

export interface ManagerReportDetailViewModel {
  id: number;
  title: string;
  status: string;
  statusModifier: string;
  isSigned: boolean;
  createdAt: string;
  companyName: string;
  reportYear: string;
  downloadUrl: string;
  metrics: ManagerReportDetailMetricRow[];
  details: Array<{ label: string; value: string }>;
  documents: ManagerReportDetailDocument[];
  signature: {
    isValid: boolean;
    signatory: string;
    organization: string;
    certAuthority: string;
    signedAt: string;
  } | null;
}

const METRIC_ROWS: Array<{
  key: keyof NonNullable<ManagerReportRow['metrics']>;
  devKey: keyof ManagerReportRow;
  label: string;
}> = [
  { key: 'revenue', devKey: 'revenue_dev', label: 'Выручка, руб' },
  {
    key: 'invest_infra',
    devKey: 'invest_infra_dev',
    label: 'Инвестиции в инфраструктуру ИНТЦ МГУ, руб',
  },
  { key: 'invest_rnd', devKey: 'invest_rnd_dev', label: 'Инвестиции в НИОКР, руб' },
  { key: 'export_share', devKey: 'export_share_dev', label: 'Доля экспорта, %' },
  {
    key: 'employees',
    devKey: 'employees_dev',
    label: 'Численность работников организации, чел.',
  },
  {
    key: 'rid',
    devKey: 'rid_dev',
    label: 'Кол-во результатов интеллектуальной деятельности, шт',
  },
];

const DEFAULT_STATUS = 'Черновик';

function displayValue(value?: string | null): string {
  return value?.trim() ? value : '—';
}

function formatReportYear(year?: string | null): string {
  return year ? `${year} г.` : '—';
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleDateString('ru-RU');
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return `${date.toLocaleDateString('ru-RU')} / ${date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function getDocumentFormat(name?: string): string {
  const extension = name?.split('.').pop()?.toUpperCase();
  return extension || 'FILE';
}

export function mapSignCheckToSignature(
  result: ManagerReportSignCheckResult,
): NonNullable<ManagerReportDetailViewModel['signature']> {
  return {
    isValid: Boolean(result.verify),
    signatory: displayValue(result.fio),
    organization: displayValue(result.nameOrganization),
    certAuthority: displayValue(result.certAuthority),
    signedAt: displayValue(result.dateSign),
  };
}

export function mapManagerReportToDetailView(
  report: ManagerReportRow,
  downloadUrl: string = getManagerReportArchiveUrl(report.id),
): ManagerReportDetailViewModel {
  const companyName = report.residentName || report.companyName || '—';
  const reportYear = report.reportYear || '';
  const status = report.status || DEFAULT_STATUS;
  const isSigned = Boolean(report.isSign ?? report.isSigned);

  return {
    id: report.id,
    title: `Отчет. ${companyName}. ${formatReportYear(reportYear)}`,
    status,
    statusModifier: getStatusModifier(status),
    isSigned,
    createdAt: formatDate(report.createdAt),
    companyName,
    reportYear: formatReportYear(reportYear),
    downloadUrl,
    metrics: METRIC_ROWS.map(({ key, devKey, label }) => ({
      label,
      plan: displayValue(report.metrics?.[key]?.plan),
      fact: displayValue(report.metrics?.[key]?.fact),
      deviation: displayValue(String(report[devKey] ?? '')),
    })),
    details: [
      { label: 'Общий комментарий', value: displayValue(report.comment) },
      { label: 'Наименование проекта', value: displayValue(report.projectNames) },
      {
        label: 'Стадия готовности реализованного НТП, по ГОСТ-Р 71726-2024',
        value: displayValue(report.projectReadiness),
      },
      {
        label: 'Перечень РИД, возникших при реализации НТП',
        value: displayValue(report.rids),
      },
      {
        label: 'Сведения о вовлечении результатов НТД в гражданский оборот',
        value: displayValue(report.commercialization),
      },
      {
        label: 'Сведения об использовании государственных мер поддержки',
        value: displayValue(report.stateSupport),
      },
    ],
    documents: (report.attachments || []).map((file) => ({
      name: file.name || 'Файл',
      url: file.url || '#',
      format: getDocumentFormat(file.name),
      size: file.size || '—',
    })),
    signature: isSigned
      ? {
          isValid: Boolean(report.verifyResult ?? true),
          signatory: displayValue(report.fioSignatory),
          organization: displayValue(report.nameOrganization ?? companyName),
          certAuthority: displayValue(report.certAuthority),
          signedAt: formatDateTime(report.dateSign ?? report.createdAt),
        }
      : null,
  };
}

export function formatManagerReportDate(iso?: string | null): string {
  return formatDate(iso);
}

const STATUS_FILTER_MODIFIER_BY_ID: Record<string, string> = {
  new: 'review',
  accepted: 'accepted',
  revision: 'revision',
  draft: 'draft',
};

export function matchesManagerReportStatusFilter(
  report: ManagerReportRow,
  statusId?: string,
): boolean {
  if (!statusId) {
    return true;
  }

  const option = MANAGER_REPORT_STATUS_OPTIONS.find((item) => item.id === statusId);
  if (!option) {
    return true;
  }

  if (report.status === option.name) {
    return true;
  }

  const expectedModifier = STATUS_FILTER_MODIFIER_BY_ID[statusId];
  return expectedModifier ? getStatusModifier(report.status) === expectedModifier : false;
}

export function filterManagerReports(
  items: ManagerReportRow[],
  params: { q?: string; filter?: ManagerReportsFilter },
): ManagerReportRow[] {
  let result = items;

  if (params.filter?.statusId) {
    result = result.filter((report) =>
      matchesManagerReportStatusFilter(report, params.filter?.statusId),
    );
  }

  const query = params.q?.trim().toLowerCase();
  if (query && query.length >= 3) {
    result = result.filter((report) =>
      [report.residentName, report.status, report.reportYear].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(query),
      ),
    );
  }

  return result;
}

export { getStatusModifier };
